/* Songbond attribution capture (ADR-029 model, as-built per SHOPIFY_THEME_PLAN
 * "Web Pixel for attribution"): pixel sandboxes can't write cart attributes, so
 * capture lives in theme JS. First/last-touch UTM + referrer + landing page ->
 * `sb_attribution` cookie (1y) -> cart attributes (attr_*) -> order
 * note_attributes -> app.songbond.com webhook (orders.first/last_touch_*).
 * Consent-gated via Shopify Customer Privacy API (ADR-043).
 * Loaded deferred AFTER pubsub.js/constants.js so PUB_SUB_EVENTS is in scope. */
(function () {
  'use strict';

  var COOKIE = 'sb_attribution';
  var MAX_AGE = 365 * 86400; // 1-year TTL per ADR-029
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var script = document.currentScript;
  var cartItemCount = script && script.dataset.sbCartItems ? parseInt(script.dataset.sbCartItems, 10) || 0 : 0;

  if (window.Shopify && window.Shopify.designMode) return; // not in the theme editor

  function readCookie() {
    var match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
    if (!match) return null;
    try {
      var data = JSON.parse(decodeURIComponent(match[1]));
      return data && data.v === 1 && data.first ? data : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(data) {
    var value = encodeURIComponent(JSON.stringify(data));
    // .songbond.com scope per the plan; host-only on preview/myshopify domains
    var domain = /(^|\.)songbond\.com$/.test(location.hostname) ? '; domain=.songbond.com' : '';
    document.cookie = COOKIE + '=' + value + domain + '; path=/; max-age=' + MAX_AGE + '; SameSite=Lax; Secure';
  }

  function newSessionId() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return 'sb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // Snapshot of this page view's attribution signal (ADR-029 touch shape).
  function currentTouch() {
    var utm = {};
    var hasUtm = false;
    var params = new URLSearchParams(location.search);
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        utm[key] = value.slice(0, 255);
        hasUtm = true;
      }
    });
    var referrer = '';
    try {
      if (document.referrer && new URL(document.referrer).hostname !== location.hostname) {
        referrer = document.referrer.slice(0, 512);
      }
    } catch (e) {}
    return {
      ts: new Date().toISOString(),
      referrer: referrer,
      landing_page: location.pathname.slice(0, 255),
      utm: utm,
      // a "touch" only counts when it carries channel info (last-non-direct-click)
      isTouch: hasUtm || referrer !== ''
    };
  }

  function asStored(touch) {
    return { ts: touch.ts, referrer: touch.referrer, landing_page: touch.landing_page, utm: touch.utm };
  }

  function updateCookie() {
    var touch = currentTouch();
    var data = readCookie();
    if (!data) {
      // first visit ever: record it even if direct (entry page + date still matter)
      data = { v: 1, sid: newSessionId(), first: asStored(touch), last: asStored(touch) };
      writeCookie(data);
    } else if (touch.isTouch) {
      data.last = asStored(touch);
      writeCookie(data);
    }
    return data;
  }

  // Flat cart-attribute contract consumed by the app repo's orders/paid webhook.
  // Last touch: attr_utm_* / attr_referrer / attr_landing_page / attr_ts
  // First touch: same keys under attr_first_*  ·  attr_sb_sid joins sessions.
  function cartAttributes(data) {
    var attrs = {};
    function put(prefix, touch) {
      if (!touch) return;
      UTM_KEYS.forEach(function (key) {
        if (touch.utm && touch.utm[key]) attrs[prefix + key] = touch.utm[key];
      });
      if (touch.referrer) attrs[prefix + 'referrer'] = touch.referrer;
      if (touch.landing_page) attrs[prefix + 'landing_page'] = touch.landing_page;
      if (touch.ts) attrs[prefix + 'ts'] = touch.ts;
    }
    put('attr_', data.last);
    put('attr_first_', data.first);
    attrs.attr_sb_sid = data.sid;
    return attrs;
  }

  function cartRoot() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  // skipMarker: cart-mutation syncs always POST (a fresh cart may carry no attrs
  // even when the fingerprint marker says "already synced" — e.g. post-purchase).
  function syncToCart(skipMarker) {
    var data = readCookie();
    if (!data) return;
    var attrs = cartAttributes(data);
    var fingerprint = JSON.stringify(attrs) + '|' + cartItemCount;
    if (!skipMarker) {
      try {
        if (localStorage.getItem('sb_attr_synced') === fingerprint) return;
      } catch (e) {}
    }
    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (response) {
        if (!response.ok) return;
        try {
          localStorage.setItem('sb_attr_synced', fingerprint);
        } catch (e) {}
      })
      .catch(function () {});
  }

  function start() {
    var data = updateCookie();
    if (!data) return;
    // cart already exists (returning visitor / server-created via brief POST)
    if (cartItemCount > 0) syncToCart(false);
    // every theme cart mutation (add-to-cart, drawer qty, quick-add) re-attaches
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        syncToCart(true);
      });
    }
  }

  function consentGranted(customerPrivacy) {
    try {
      if (typeof customerPrivacy.analyticsProcessingAllowed === 'function') {
        return customerPrivacy.analyticsProcessingAllowed();
      }
      if (typeof customerPrivacy.userCanBeTracked === 'function') {
        return customerPrivacy.userCanBeTracked();
      }
    } catch (e) {}
    return true;
  }

  function init() {
    var shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      start();
      return;
    }
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      // per Shopify docs: if the Customer Privacy API is unavailable, tracking may proceed
      if (error || !shopify.customerPrivacy) {
        start();
        return;
      }
      if (consentGranted(shopify.customerPrivacy)) {
        start();
        return;
      }
      document.addEventListener('visitorConsentCollected', function onConsent() {
        if (consentGranted(shopify.customerPrivacy)) {
          document.removeEventListener('visitorConsentCollected', onConsent);
          start();
        }
      });
    });
  }

  init();
})();
